/**
 * ArcGIS Pro–style georeference tool: pick a point on the unreferenced
 * image, then the matching point on the earth. Three complete pairs are
 * enough to compute an affine and overlap the image.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Button, Callout, Chip, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import {
  bboxFromCorners,
  readGeoreference,
  type GeoreferenceControlPoint,
  type LonLat,
} from './georeferenceTypes';
import {
  computeAffineFromControlPoints,
  cornersFromExtent,
  isUsableExtent,
  transformCorners,
} from './georeferenceTransform';
import { georeferencePreviewUrl, georeferenceTileJsonUrl } from './georeferencePreview';
import { withGeoreferenceStyle } from './needsGeoreference';
import {
  applyGeoreferenceImagePlacement,
  resolveGeoreferenceDrawSurface,
  type DraftControlPoint,
} from './georeferenceSurface';

const ATTACH_RETRY_MS = 120;
const ATTACH_MAX_ATTEMPTS = 20;
const MIN_POINTS = 3;

type PickPhase = 'idle' | 'source' | 'target';

function newPointId(): string {
  return `gcp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatCoord(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  return value.toFixed(5);
}

export interface GeoreferencePanelProps {
  readonly layer: Layer;
  readonly container?: HTMLElement | null;
  readonly readOnly?: boolean;
  readonly onCommit?: (layer: Layer) => void;
}

export function GeoreferencePanel({
  layer,
  container = null,
  readOnly = false,
  onCommit,
}: GeoreferencePanelProps) {
  const patchCatalogLayer = useViewerStore((s) => s.patchCatalogLayer);
  const stored = readGeoreference(layer);
  const [points, setPoints] = useState<DraftControlPoint[]>(() =>
    (stored?.controlPoints ?? []).map((point) => ({
      id: point.id,
      source: point.source,
      target: point.target,
    })),
  );
  const [phase, setPhase] = useState<PickPhase>('idle');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [extent, setExtent] = useState<readonly [number, number, number, number] | null>(
    () => (isUsableExtent(layer.bbox) ? layer.bbox : null),
  );
  const [, setError] = useState<string | null>(null);
  const [applyMessage, setApplyMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isUsableExtent(layer.bbox)) {
      setExtent(layer.bbox);
      return;
    }
    const tilejsonUrl = georeferenceTileJsonUrl(layer.endpoints.tiles);
    if (!tilejsonUrl) return;
    let cancelled = false;
    void fetch(tilejsonUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((json: { bounds?: number[] } | null) => {
        if (cancelled || !json?.bounds || !isUsableExtent(json.bounds)) return;
        setExtent([json.bounds[0], json.bounds[1], json.bounds[2], json.bounds[3]]);
      })
      .catch(() => {
        /* keep current extent */
      });
    return () => {
      cancelled = true;
    };
  }, [layer.bbox, layer.endpoints.tiles, layer.id]);

  const completePairs = useMemo(
    () =>
      points.filter(
        (point): point is DraftControlPoint & { source: LonLat; target: LonLat } =>
          point.source !== null && point.target !== null,
      ),
    [points],
  );

  const fit = useMemo(
    () =>
      computeAffineFromControlPoints(
        completePairs.map((point) => ({ source: point.source, target: point.target })),
      ),
    [completePairs],
  );

  useEffect(() => {
    const surface = resolveGeoreferenceDrawSurface(container);
    surface?.render(points);
  }, [container, points]);

  useEffect(
    () => () => {
      resolveGeoreferenceDrawSurface(container)?.clear();
    },
    [container],
  );

  useEffect(() => {
    if (phase === 'idle') return;
    let detach: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      detach = resolveGeoreferenceDrawSurface(container)?.attach({
        onPoint: (lonLat) => {
          setPoints((current) => {
            const workingId = activeId ?? current[current.length - 1]?.id;
            return current.map((point) => {
              if (point.id !== workingId) return point;
              if (phase === 'source' && !point.source) return { ...point, source: lonLat };
              if (phase === 'target' && point.source && !point.target) {
                return { ...point, target: lonLat };
              }
              return point;
            });
          });
          setError(null);
          setApplyMessage(null);
          if (phase === 'source') {
            setPhase('target');
            return;
          }
          setPhase('idle');
          setActiveId(null);
        },
      }) ?? null;
      if (detach) return;
      attempts += 1;
      if (attempts >= ATTACH_MAX_ATTEMPTS) {
        setError('Georeference picking needs the 2D map view — switch to 2D map and try again.');
        setPhase('idle');
        return;
      }
      retryTimer = setTimeout(attach, ATTACH_RETRY_MS);
    };
    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      detach?.();
    };
  }, [activeId, container, phase]);

  const startPair = useCallback(() => {
    if (readOnly) return;
    const id = newPointId();
    setPoints((current) => [...current, { id, source: null, target: null }]);
    setActiveId(id);
    setPhase('source');
    setError(null);
    setApplyMessage(null);
  }, [readOnly]);

  const removePair = useCallback((id: string) => {
    setPoints((current) => current.filter((point) => point.id !== id));
    if (activeId === id) {
      setActiveId(null);
      setPhase('idle');
    }
  }, [activeId]);

  const apply = useCallback(() => {
    if (readOnly) return;
    if (!fit || !extent) {
      setError(
        !extent
          ? 'Could not read the image extent. Zoom to the layer and try again.'
          : `Add at least ${MIN_POINTS} complete control points.`,
      );
      return;
    }
    const corners = transformCorners(fit.affine, cornersFromExtent(extent));
    const bbox = bboxFromCorners(corners);
    if (!bbox) {
      setError('The transform did not produce a usable image footprint.');
      return;
    }
    const controlPoints: GeoreferenceControlPoint[] = completePairs.map((point) => ({
      id: point.id,
      source: point.source,
      target: point.target,
    }));
    const previewUrl = georeferencePreviewUrl(layer.endpoints.tiles);
    const next = withGeoreferenceStyle(
      layer,
      {
        status: 'applied',
        controlPoints,
        affine: fit.affine,
        corners,
        bbox,
        sourceCrs: layer.crs || 'LOCAL',
        targetCrs: 'EPSG:4326',
        rmse: fit.rmse,
        previewUrl,
      },
      { crs: 'EPSG:4326', sourceCrs: 'EPSG:4326', bbox },
    );
    patchCatalogLayer(layer.id, () => next);
    onCommit?.(next);
    let placed = false;
    if (previewUrl) {
      placed = applyGeoreferenceImagePlacement(container, layer.id, previewUrl, corners);
    }
    setApplyMessage(
      placed
        ? `Image positioned with ${controlPoints.length} control points (RMSE ${fit.rmse.toFixed(6)}°).`
        : `Georeference saved with ${controlPoints.length} control points. Reload the layer if the image has not moved yet.`,
    );
    setPhase('idle');
  }, [completePairs, container, extent, fit, layer, onCommit, patchCatalogLayer, readOnly]);

  const reset = useCallback(() => {
    setPoints([]);
    setActiveId(null);
    setPhase('idle');
    setError(null);
    setApplyMessage(null);
    const next = withGeoreferenceStyle(layer, { status: 'rectifying' });
    patchCatalogLayer(layer.id, () => next);
    onCommit?.(next);
  }, [layer, onCommit, patchCatalogLayer]);

  const alreadyApplied = stored?.status === 'applied';

  return (
    <div
      data-testid="georeference-panel"
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}
    >
      <Callout severity="info" icon="pin_drop">
        Click a recognizable point on the image, then click the same location
        on the earth. Repeat until you have at least {MIN_POINTS} pairs, then
        apply to overlap the image.
      </Callout>

      {phase === 'source' ? (
        <Chip icon="my_location" variant="info" label="Click the point on the image" />
      ) : null}
      {phase === 'target' ? (
        <Chip icon="public" variant="info" label="Click the matching point on the earth" />
      ) : null}
      {alreadyApplied && stored?.rmse != null ? (
        <Chip
          icon="check_circle"
          variant="success"
          label={`Applied · RMSE ${stored.rmse.toFixed(6)}°`}
        />
      ) : null}

      {completePairs.length >= MIN_POINTS && !fit ? (
        <Callout severity="warning">
          Those points cannot define a transform — they may be collinear. Add
          another pair or pick more spread-out locations.
        </Callout>
      ) : null}
      {applyMessage ? <Callout severity="success">{applyMessage}</Callout> : null}

      {points.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr 1fr auto',
            gap: tokens.space[1],
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.primary,
          }}
        >
          <span style={{ color: tokens.color.ink.muted }}>#</span>
          <span style={{ color: tokens.color.ink.muted }}>From (image)</span>
          <span style={{ color: tokens.color.ink.muted }}>To (earth)</span>
          <span />
          {points.map((point, index) => (
            <div key={point.id} style={{ display: 'contents' }}>
              <span data-testid={`georeference-row-${index}`}>{index + 1}</span>
              <span>
                {formatCoord(point.source?.[0])}, {formatCoord(point.source?.[1])}
              </span>
              <span>
                {formatCoord(point.target?.[0])}, {formatCoord(point.target?.[1])}
              </span>
              <button
                type="button"
                aria-label={`Remove control point ${index + 1}`}
                data-testid={`georeference-remove-${index}`}
                onClick={() => removePair(point.id)}
                disabled={readOnly}
                style={{
                  minHeight: tokens.density.touchMin,
                  border: 'none',
                  background: 'transparent',
                  color: tokens.color.ink.secondary,
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[2] }}>
        <Button
          type="button"
          variant="secondary"
          data-testid="georeference-add-point"
          onClick={startPair}
          disabled={readOnly || phase !== 'idle'}
        >
          Add control point
        </Button>
        <Button
          type="button"
          variant="primary"
          data-testid="georeference-apply"
          onClick={apply}
          disabled={readOnly || !fit || phase !== 'idle'}
        >
          Apply georeference
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-testid="georeference-reset"
          onClick={reset}
          disabled={readOnly || (points.length === 0 && !alreadyApplied)}
        >
          Reset
        </Button>
      </div>
      <span
        style={{
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.engMeta.size,
          color: tokens.color.ink.muted,
        }}
      >
        {completePairs.length} of {MIN_POINTS} required pairs
        {fit ? ` · RMSE ${fit.rmse.toFixed(6)}°` : ''}
      </span>
    </div>
  );
}
